import { cn } from '@askable/ui/lib/utils';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useFeatureFlags } from 'feature-flags';
import { User, MessageSquareText, MousePointerClick, Video, Repeat2 } from 'lucide-react';
import { forwardRef, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { Link } from 'src/lib/routing';
import { match } from 'ts-pattern';

import { prefersClassicUi } from 'containers/FocusGroups/utils/classic-ui';
import { BOOKING_TYPE, NOTIFICATION_TYPE, NOTIFICATION_SUBTYPE } from 'lib/constants';

import { notificationLink } from './notification-link';

import type { NotificationType } from './NotificationsContent';

type NotificationsItemProps = Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'onClick'> & {
  index?: number;
  item: NotificationType;
  onClick: (itemId: string) => void;
};

const BookingIcons: Record<number, React.ElementType> = {
  [BOOKING_TYPE.IN_PERSON]: User,
  [BOOKING_TYPE.REMOTE]: Video,
  [BOOKING_TYPE.ONLINE]: MousePointerClick,
  [BOOKING_TYPE.LONGITUDINAL_STUDY]: Repeat2,
  [BOOKING_TYPE.UNMODERATED]: MousePointerClick,
  [BOOKING_TYPE.AI_MODERATED]: MessageSquareText,
  [BOOKING_TYPE.SURVEY]: MousePointerClick,
};

export const NotificationsItem = forwardRef<HTMLAnchorElement, NotificationsItemProps>(
  ({ item, index, onClick, className, ...props }, ref) => {
    const timezone = item.context?.booking?.config?.timezone || 'Australia/Brisbane';
    const BookingIcon = BookingIcons[item.context?.booking?.type ?? BOOKING_TYPE.REMOTE] ?? Video;
    const { llm_lab_features } = useFeatureFlags(['llm_lab_features']);

    const link = useMemo(
      () =>
        notificationLink({
          link: item.link,
          type: item.type,
          booking: item.context?.booking,
          isLabsEnabled: !!llm_lab_features,
          prefersClassic: prefersClassicUi(),
        }),
      [item.link, item.type, item.context?.booking, llm_lab_features],
    );

    const sessionTime = useMemo(() => {
      if (!item.context?.participant_session?.start) {
        return null;
      }

      const start = formatInTimeZone(new Date(item.context.participant_session.start), timezone, 'EEEE h:mma');
      const end = item.context.participant_session.end
        ? formatInTimeZone(new Date(item.context.participant_session.end), timezone, 'h:mma')
        : null;

      return { start, end };
    }, [item.context?.participant_session, timezone]);

    const content = useMemo(() => {
      if (!item.context) {
        return null;
      }

      return match([item.type, item.subtype])
        .with([NOTIFICATION_TYPE.MESSAGES, NOTIFICATION_SUBTYPE.NEW_MESSAGE_FROM_PARTICIPANT], () =>
          item.context?.messages?.body ? item.context.messages.body : null,
        )
        .with([NOTIFICATION_TYPE.BOOKING_PARTICIPANT, NOTIFICATION_SUBTYPE.BOOKING_PARTICIPANT_FEEDBACK], () =>
          item.context?.booking_participant?.feedback?.participant
            ? item.context.booking_participant.feedback.participant
            : null,
        )
        .with([NOTIFICATION_TYPE.BOOKING_PARTICIPANT, NOTIFICATION_SUBTYPE.BOOKING_PARTICIPANT_CANCELLED], () =>
          item.context?.booking_participant?.cancel_reason ? item.context.booking_participant.cancel_reason : null,
        )
        .otherwise(() => null);
    }, [item.type, item.subtype, item.context]);

    return (
      <Link
        ref={ref}
        to={link}
        className={cn(
          className,
          'relative flex items-start gap-2 rounded-none px-2 py-3 text-left font-normal no-underline',
          'hover:bg-secondary active:bg-border/60',
          {
            'text-muted-foreground': item.read,
            'border-none': index === 0,
          },
        )}
        onClick={() => onClick(item._id)}
        data-notification-index={index}
        {...props}
      >
        <div
          className={cn('mt-1 h-2 w-2 shrink-0 rounded-full', {
            'bg-secondary': item.read,
            'bg-chart-1': !item.read,
          })}
          data-testid="read_notification"
          data-read={item.read}
        />
        <div className="flex flex-1 flex-col gap-1">
          <div
            // The message is markdown, so emphasis becomes <strong> at the browser's
            // 700. The design system stops at 600, so hold <strong> to semibold.
            className={cn('whitespace-normal text-sm [&_strong]:font-semibold', {
              'font-semibold': !item.read,
            })}
          >
            <ReactMarkdown>{item.message}</ReactMarkdown>
          </div>

          {item.context?.booking ? (
            <div className="flex gap-2">
              <BookingIcon className="h-4 w-4 text-muted-foreground" />
              {item.context.booking.name ? (
                <div className="whitespace-normal text-sm">{item.context.booking.name}</div>
              ) : null}
            </div>
          ) : null}

          {content}

          {sessionTime ? (
            <div className="flex gap-2">
              <time
                dateTime={
                  item.context?.participant_session?.start
                    ? new Date(item.context.participant_session.start).toISOString()
                    : undefined
                }
              >
                {sessionTime.start}
              </time>
              {sessionTime.end && item.context?.participant_session?.end ? (
                <>
                  -
                  <time dateTime={new Date(item.context.participant_session.end).toISOString()}>{sessionTime.end}</time>
                </>
              ) : null}
            </div>
          ) : null}

          {item.created ? (
            <time dateTime={new Date(item.created).toISOString()} className="mt-1 text-xs">
              {format(new Date(item.created), 'PPp')}
            </time>
          ) : null}
        </div>
      </Link>
    );
  },
);

NotificationsItem.displayName = 'NotificationsItem';
